// Libraries
import React from 'react';

// Supermove
import {Icon, Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useDrawer, useResponsive} from '@supermove/hooks';
import {ClientModel, ProjectModel} from '@supermove/models';
import {withFragment} from '@supermove/utils';

// App
import IconButton from '@shared/design/components/IconButton';
import Line from '@shared/design/components/Line';
import SkeletonLoader from '@shared/design/components/SkeletonLoader';
import Text from '@shared/design/components/Text';
import EditProjectClientsDrawer from '@shared/modules/Project/components/widgets/components/EditProjectClientsDrawer';
import ProjectDetailsBlockHeader, {
  BlockHeaderLoader,
  EditButton,
} from '@shared/modules/Project/components/widgets/components/ProjectDetailsBlockHeader';
import ProjectDetailsBlockWrapper from '@shared/modules/Project/components/widgets/components/ProjectDetailsBlockWrapper';

const Column = Styled.View`
`;

const Row = Styled.View`
  flex-direction: row;
`;

const SkeletonComponent = () => {
  return (
    <React.Fragment>
      <BlockHeaderLoader
        title={'Client & Billing Information'}
        ActionsComponent={() => <EditButton isDisabled />}
      />
      <ClientFieldsLoading title={'Client Information'} />
      <Space height={16} />
      <Line />
      <Space height={16} />
      <ClientFieldsLoading title={'Billing Information'} />
    </React.Fragment>
  );
};

const ClientFieldsLoading = ({title}: any) => {
  const responsive = useResponsive();

  return (
    <React.Fragment>
      <Text.MicroLabel>{title}</Text.MicroLabel>
      <Space height={4} />
      <SkeletonLoader
        height={
          responsive.desktop
            ? SkeletonLoader.HEIGHT.Typography.Body
            : SkeletonLoader.HEIGHT.Typography.Subheading
        }
        width={200}
      />
    </React.Fragment>
  );
};

const ClientFields = ({title, client, isBilling}: any) => {
  return (
    <React.Fragment>
      <Text.MicroLabel>{title}</Text.MicroLabel>
      <Space height={4} />
      {isBilling && !client ? (
        <Text.Body>Same as client</Text.Body>
      ) : (
        <React.Fragment>
          <Text.Body>{client.primaryContact.fullName}</Text.Body>
          {client.name && (
            <React.Fragment>
              <Space height={2} />
              <Text.Micro>{client.name}</Text.Micro>
            </React.Fragment>
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

const ClientInformation = withFragment(
  ({
    title,
    client,
    handleViewClient,
    isBilling,
  }: {
    title: string;
    client?: ClientModel;
    handleViewClient?: () => void;
    isBilling?: boolean;
  }) => {
    return (
      <Row>
        <Column style={{flex: 1}}>
          <ClientFields title={title} client={client} isBilling={isBilling} />
        </Column>
        {client && handleViewClient && (
          <IconButton source={Icon.InfoCircle} onPress={handleViewClient} />
        )}
      </Row>
    );
  },
  gql`
    fragment ProjectDetailsClientsBlock_ClientInformation on Client {
      id
      name
      primaryContact {
        id
        fullName
      }
    }
  `,
);

const EditClientsButton = ({project}: {project: ProjectModel}) => {
  const editProjectClientsDrawer = useDrawer({name: 'Edit Project Clients Drawer'});

  return (
    <React.Fragment>
      <EditButton onPress={editProjectClientsDrawer.handleOpen} />
      <EditProjectClientsDrawer
        key={editProjectClientsDrawer.key}
        isOpen={editProjectClientsDrawer.isOpen}
        handleClose={editProjectClientsDrawer.handleClose}
        project={project}
      />
    </React.Fragment>
  );
};

const ProjectDetailsClientsBlockContent = ({
  project,
  isSameBillingClient,
  handleViewClient,
}: {
  project: ProjectModel;
  isSameBillingClient: boolean;
  handleViewClient?: () => void;
}) => {
  return (
    <Column>
      <ProjectDetailsBlockHeader
        title={`Client & Billing Information`}
        ActionsComponent={EditClientsButton}
        actionsComponentProps={{project}}
      />
      <ClientInformation
        title={`Client Information`}
        client={project.client}
        handleViewClient={handleViewClient}
      />
      <Space height={16} />
      <Line />
      <Space height={16} />
      <ClientInformation
        title={`Billing Information`}
        client={isSameBillingClient ? undefined : project.billingClient}
        handleViewClient={handleViewClient}
        isBilling
      />
    </Column>
  );
};

const ProjectDetailsClientsBlock = ({
  projectUuid,
  index,
  handleViewClient,
}: {
  projectUuid: string;
  index: number;
  handleViewClient?: () => void;
}) => {
  return (
    <ProjectDetailsBlockWrapper
      index={index}
      query={ProjectDetailsClientsBlock.query}
      queryVariables={{projectUuid}}
      SkeletonComponent={SkeletonComponent}
    >
      {({data}: {data: {project: ProjectModel}}) => {
        const {project} = data;
        const isSameBillingClient = project.client.id === project.billingClient.id;

        return (
          <ProjectDetailsClientsBlockContent
            project={project}
            isSameBillingClient={isSameBillingClient}
            handleViewClient={handleViewClient}
          />
        );
      }}
    </ProjectDetailsBlockWrapper>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
ProjectDetailsClientsBlock.query = gql`
  ${ClientInformation.fragment}
  ${EditProjectClientsDrawer.fragment}

  query ProjectDetailsClientsBlock($projectUuid: String!) {
    ${gql.query}
    project(uuid: $projectUuid) {
      id
      client {
        id
        ...ProjectDetailsClientsBlock_ClientInformation
      }
      billingClient {
        id
        ...ProjectDetailsClientsBlock_ClientInformation
      }
      ...EditProjectClientsDrawer
    }
  }
`;

export default ProjectDetailsClientsBlock;
